import { AlertColor, Button, CircularProgress, Grid, Tab, Tabs, Typography } from '@mui/material';
import { Box } from '@mui/system';
import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import strings from '../../../../../common/Translation/Translate';
import Notification from '../../../../../components/Notification';
import Upload from '../../../../../components/Upload/Upload';
import Config from '../../../../../utils/Config';
import axiosInstance from '../../../../../utils/axios';
import { dataFlag } from '../../../../../utils/constants/data';
import { routes } from '../../../../../utils/constants/routes';
import { URLS } from '../../../../../utils/constants/urls';
import Navigation from '../../../../../utils/hooks/Navigation';
import { languages } from '../../../../../utils/redux/reducer/authentication-slice';
import { TabPanel } from '../../../../../utils/validators/HelperComponents';
import { createDropdownData } from '../../../../../utils/validators/HelperFunctions';
import VenueInfoTab from './VenueInfoTab';
import PageLoader from 'components/PageLoader';
import { pageTitleSx } from 'utils/styles/pageHeader';

const AddEditVenue: FC = () => {
  const { id } = useParams() as any;
  const Navigate = useNavigate();
  const queryStings = useLocation().search as any;
  const queryStingId = new URLSearchParams(queryStings).get('id') as string;
  const languageList = useSelector(languages) as any;
  const [pageResponse, setPageResponse] = useState<any>([]);
  const [deletedImage, setDeletedImage] = useState<any>([]);
  const [value, setValue] = React.useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [customId, setCustomId] = useState<number>();
  const [taxGroups, setTaxGroups] = useState<any>([]);
  const [venueType, setVenueType] = useState<any>([]);
  const [parentVenue, setParentVenue] = useState<any>([]);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({
    display: false,
    severity: null,
    message: '',
  });
  const closeNotification = (value: boolean) => {
    setMessage({ ...message, display: value });
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([getTaxGroups(), getVenueType(), getParentVenue()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  }, []);

  const getTaxGroups = async () => {
    const { data } = await axiosInstance.get(URLS.taxGroupDropdown);
    setTaxGroups(data);
  };

  const getVenueType = async () => {
    const { data } = await axiosInstance.get(URLS.venueTypes);
    setVenueType(data);
  };

  const getParentVenue = async () => {
    const { data } = await axiosInstance.get(URLS.venues);
    const filteredVenues = id ? data.filter((venue: any) => venue.id !== parseInt(id)) : data;
    setParentVenue(filteredVenues);
  };

  const dropdowonData = {
    taxGroupOnGuestCharge: createDropdownData(taxGroups, ['id', 'name']),
    taxGroupOnHallRental: createDropdownData(taxGroups, ['id', 'name']),
    taxGroupOnServiceCharge: createDropdownData(taxGroups, ['id', 'name']),
    available: createDropdownData(dataFlag(strings), ['id', 'name']),
    parentVenueId: createDropdownData(parentVenue, ['id', 'name']),
    venueTypeId: createDropdownData(venueType, ['id', 'name']),
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getPageData = async (paramId: any) => {
    try {
      setLoading(true);
      const pageId = id || customId || paramId || queryStingId;
      const { data } = await axiosInstance.get(URLS.venue_edit(pageId));
      const container: any = {};
      for (let i = 0; i < languageList?.length; ++i) {
        let desc = ['name_' + languageList[i]?.value];
        let values = ['description_' + languageList[i]?.value];
        container[desc as any] = data.localizedAttributes[languageList[i]?.value]
          ? data.localizedAttributes[languageList[i]?.value].description
          : '';
        container[values as any] = data.localizedAttributes[languageList[i]?.value]
          ? data.localizedAttributes[languageList[i]?.value].name
          : '';
      }
      const fileData = data.media;
      const newFiles = pageResponse?.files?.length > 0 ? pageResponse?.files : [];
      if (fileData.length > 0) {
        fileData.forEach((item: any) => {
          const objFile = {} as any;
          objFile.preview = fileData ? Config.BaseURL + item?.url : null;
          objFile.name = item?.fileName || '';
          objFile.id = item.id;
          newFiles.push(objFile);
        });
      }
      const unique = newFiles?.filter((obj: any, index: number) => {
        return index === newFiles.findIndex((o: any) => obj.name === o.name);
      });
      setPageResponse({ ...data, ...container, files: unique });
    } catch (error: any) {
      if (error.response) {
        setMessage({
          display: true,
          severity: 'error',
          message: error.response.data?.error_description
            ? error.response.data.error_description
            : error.response.data.message,
        });
      } else {
        setMessage({
          display: true,
          severity: 'error',
          message: error.message,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      if (id || queryStingId) {
        setCustomId(id);
        await getPageData(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, queryStingId]);

  const getFileDetails = (
    value: string,
    field: string,
    error?: { error: boolean; message: string },
    isDeleted?: any,
  ) => {
    const deletedArray = [...deletedImage] as any;
    if (Array.isArray(isDeleted)) {
      if (isDeleted[0]?.id) {
        deletedArray.push(isDeleted[0]?.id);
      }
      setDeletedImage(deletedArray);
      setPageResponse({ ...pageResponse, files: value });
    } else {
      setPageResponse({ ...pageResponse, files: value });
    }
  };

  const handleUpload = async () => {
    if (pageResponse?.files?.length > 0 || deletedImage || deletedImage === undefined) {
      try {
        setLoading(true);
        const body = new FormData();
        pageResponse?.files?.forEach((file: any) => {
          body.append('files', file);
        });
        if (pageResponse?.files?.length === 0) {
          body.append('files', null as any);
        }

        body.append('deletedFiles', deletedImage);
        const config = {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        };
        const { status, data } = await axiosInstance.put(
          URLS.uploadVenueImage(id || customId || queryStingId),
          body,
          config,
        );
        if (status === 200 || status === 201) {
          setLoading(false);
          setDeletedImage('');
          getPageData(data.id as number);
          setMessage({
            display: true,
            severity: 'success',
            message: strings.addOn_upload_image_success_text,
          });
          setTimeout(() => {
            Navigate(routes.venue);
          }, 1000);
        }
      } catch (e: any) {
        if (e.response) {
          setMessage({
            display: true,
            severity: 'error',
            message: e.response?.data.message,
          });
        } else {
          setMessage({
            display: true,
            severity: 'error',
            message: e.message,
          });
        }
        setLoading(false);
      }
    }
  };
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  return (
    <>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
        />
      )}
      <Grid container>
        <Grid item xs={10}>
          <Typography sx={pageTitleSx}>
            {' '}
            {id || queryStingId ? strings.venue_title_edit : strings.venue_title_create}{' '}
          </Typography>
        </Grid>
        <Grid item textAlign="right" xs={2}>
          <Navigation url={routes.venue} />
        </Grid>
      </Grid>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
          <Tab label={strings.venue_tab_information_text} {...a11yProps(0)} />
          <Tab
            disabled={queryStingId === null && customId === undefined && id === undefined}
            label={strings.upload_image_text}
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <VenueInfoTab getPageData={getPageData} dropdownData={dropdowonData} pageResponseData={pageResponse} />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <Upload multiline={true} inputValue={pageResponse?.files} textChange={getFileDetails} accept={['image/*']} />
        <Grid container sx={{ my: 5 }}>
          <Button
            disabled={pageResponse?.files?.length === 0 && deletedImage === ''}
            variant="contained"
            sx={{ color: '#ffffff' }}
            onClick={handleUpload}>
            {loading && <CircularProgress color="inherit" sx={{ mr: 1 }} size={20} />}
            {strings.upload_image_button}
          </Button>
        </Grid>
      </TabPanel>
    </>
  );
};
export default AddEditVenue;
